import React, { useState, useEffect, useRef } from 'react';
import { setPageMeta } from '../utils/seo';
import { trackVideoEvent } from '../utils/analytics';
import Features from '../components/Features';
import ProjectManagement from '../components/ProjectManagement';
import WorkerProfiles from '../components/WorkerProfiles';
import FeedbackForm from '../components/FeedbackForm';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  ClipboardText, 
  Users, 
  CurrencyDollar, 
  Translate, 
  ChatCircle, 
  Globe, 
  SquaresFour, 
  Microphone, 
  Play, 
  SpeakerHigh, 
  Headphones, 
  FileText, 
  PaperPlaneTilt, 
  CheckCircle, 
  DownloadSimple, 
  Calculator, 
  Receipt, 
  CaretLeft, 
  CaretRight, 
  Sparkle,
  CalendarBlank,
  CalendarCheck,
  Clock,
  Briefcase,
  Bell,
  MapPin,
  Camera,
  Image,
  Folder,
  FolderOpen,
  ShieldCheck,
  ChartPie,
  TrendUp,
  ChartBar,
  Warning,
  ArrowsClockwise,
  Cube,
  Package,
  Wrench,
  Timer,
  Coffee,
  ClockCounterClockwise,
  Export,
  Wallet,
  Bank,
  Lightning,
  CreditCard,
  ArrowsLeftRight,
  Coins,
  FilePdf,
  Blueprint,
  Certificate,
  Lock,
  Cloud,
  Upload,
  Handshake,
  House,
  Eye,
  Star,
  Envelope,
  Phone,
  ThumbsUp,
  UsersThree,
  UserPlus,
  Crown,
  Scales,
  ShoppingCart,
  Storefront,
  CloudSun,
  TelegramLogo,
  Dog
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { VoiceTranslatorMock } from '../components/landing/VoiceTranslatorMock';
import ShoppingListPromo from '../components/landing/ShoppingListPromo';

const Home = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  const newFeatures = [
    {
      id: 'concrete-calculator',
      title: 'Free Concrete Calculator',
      subtitle: 'Yards, Meters & Bags — No Math Required.',
      description: 'Slabs, footings, columns and steps: enter your pour dimensions and get cubic yards, cubic meters, and exactly how many 40/60/80 lb bags to buy — waste factor included, imperial or metric.',
      icon: Cube,
      color: 'bg-[#D95800]',
      features: ['4 pour shapes including stairs', 'Bag counts for 40/60/80 lb mix', 'Imperial & metric units', 'Ready-mix advice for big pours'],
      cta: 'Calculate Concrete',
      link: '/tools/concrete-calculator'
    },
    {
      id: 'change-orders',
      title: 'AI Change Orders',
      subtitle: 'Scope Changed? Price It In 60 Seconds.',
      description: 'Client added a second vanity? Describe the change and AI writes an itemized change order — line items, change total, new project total and timeline impact — calibrated to your local market.',
      icon: ArrowsClockwise,
      color: 'bg-[#2B4C6F]',
      features: ['Itemized line items with labor & materials', 'New project total computed instantly', 'Local cost calibration built in', 'Price a fix straight from Site Problems'],
      cta: 'Price a Change',
      link: '/ai-pricing'
    },
    {
      id: 'site-problems',
      title: 'On-Site Problem Management',
      subtitle: 'Photo It. Assign It. Fix It.',
      description: 'Crews snap a photo of any job-site problem, set the severity, and AI instantly suggests the likely cause, fix steps, safety flags, and a rough repair cost. Assign it to a worker and track it to resolved.',
      icon: Warning,
      color: 'bg-[#D95800]',
      features: ['Photo reports from any phone', 'AI cause, fix & cost advice', 'Assign to crew & track status', 'Alerts on report, assign & resolve'],
      cta: 'Open Site Problems',
      link: '/site-problems'
    },
    {
      id: 'blueprint-ai',
      title: 'AI Reads Blueprints',
      subtitle: 'PDF plans → priced estimate.',
      description: 'Upload your 2D CAD PDF drawings to the AI Pricing Tool. It reads dimensions, rooms and notes across up to 5 files, builds a room-by-room material takeoff with waste factors, and prices the whole job in minutes.',
      icon: Blueprint,
      color: 'bg-[#2B4C6F]',
      features: ['Reads floor plans & elevations', 'Material takeoff with quantities', 'Auto-fills the estimate form', 'Up to 5 PDFs per project'],
      cta: 'Read the Announcement',
      link: '/blog/ai-reads-blueprints-estimates'
    },
    {
      id: 'buddy',
      title: 'Meet Buddy',
      subtitle: 'Your 24/7 AI Helper.',
      description: 'Our new mascot is on duty! Buddy answers any question about ConstructHelp in seconds — how leads work, how escrow protects you, where to find anything. Free for everyone, no login needed, and he fetches a human when you need one.',
      icon: Dog,
      color: 'bg-amber-500',
      features: ['Instant answers, day or night', 'Free — no account needed', 'Speaks your language', 'One tap to reach a real human'],
      cta: 'Read the Announcement',
      link: '/blog/meet-buddy-ai-helper'
    },
    {
      id: 'subcontractors',
      title: 'Hire Subcontractors',
      subtitle: 'Build Your Crew of Subs.',
      description: 'Post jobs open to licensed subcontractors — not just workers. Work as a sub on big projects while running your own crew of subs underneath you, with every job linked to its parent project so the whole chain stays organized.',
      icon: UsersThree,
      color: 'bg-[#EA580C]',
      features: ['Jobs open to Workers, Subcontractors, or Both', 'Link sub jobs to the parent project', 'Subcontracting hub: My Subs + Jobs I\'m Subbing On', 'Find licensed subs in the directory'],
      cta: 'Read the Announcement',
      link: '/blog/hire-subcontractors-build-your-crew'
    },
    {
      id: 'voice-dictation',
      title: 'Voice Dictation',
      subtitle: 'Gloves On? Just Talk.',
      description: 'Tap the mic and talk — we type for you. Dictate job postings, marketplace listings, and even full AI pricing scope checklists out loud, in English or Spanish. The AI cleans up filler words and keeps every measurement exactly as spoken.',
      icon: Microphone,
      color: 'bg-[#D95800]',
      features: ['Dictate job posts, listings & pricing checklists', 'English & Español auto-detect', 'AI splits speech into checklist items', 'Nothing to install — works on your phone'],
      cta: 'Read the Announcement',
      link: '/blog/post-jobs-price-work-by-voice'
    },
    {
      id: 'telegram-app',
      title: 'ConstructHelp on Telegram',
      subtitle: 'The Whole Platform, Inside Telegram',
      description: 'Open our bot, tap Start, and you\'re in — no email, no password. Post projects, find pros, chat, and shop the marketplace, with every job alert, offer and message pinging you instantly in your Telegram chats.',
      icon: TelegramLogo,
      color: 'bg-[#1C93E3]',
      features: ['One-tap sign in with Telegram', 'Instant alerts in your chats', 'Same account as the website', 'Scan the QR on our homepage'],
      cta: 'Open in Telegram',
      link: 'https://t.me/Constructhelp123bot'
    },
    {
      id: 'weather-forecaster',
      title: 'Job Weather Forecaster',
      subtitle: 'Daily Rain Checks for Every Job Site',
      description: 'A 7-day forecast for your city and every active job site — with automatic morning rain alerts so your crew never shows up to a washed-out day. Rain warnings appear right on your Crew Calendar and job pages.',
      icon: CloudSun,
      color: 'bg-sky-600',
      features: ['Daily automatic rain check every morning', 'Rain warnings on the Crew Calendar', '7-day job-site forecast on every job page', 'Free — no setup needed'],
      cta: 'See Your Dashboard',
      link: '/contractor-dashboard'
    },
    {
      id: 'tools-marketplace',
      title: 'Tools & Materials Marketplace',
      subtitle: 'Buy & Sell Gear and Leftover Materials',
      description: 'List used tools, extra lumber, or leftover supplies in minutes. Buyers pay securely through the platform with Stripe, or message you to arrange pickup — earnings go straight to your wallet.',
      icon: Storefront,
      color: 'bg-[#B45309]',
      features: ['Free to list — anyone can sell', 'Secure Stripe checkout', 'Photos, filters & local pickup', 'Earnings credited to your wallet'],
      cta: 'Browse the Marketplace',
      link: '/marketplace'
    },
    {
      id: 'voice-translator',
      title: 'Voice Translator',
      subtitle: 'Speak Your Language. They Hear Theirs.',
      description: 'Tap the mic, talk, and hear the translation spoken out loud in seconds — any pair of 12 languages, right on the job site. Trade terms translate correctly every time.',
      icon: Microphone,
      color: 'bg-emerald-700',
      features: ['12 languages, voice-to-voice', 'Whisper AI transcription', 'Trade terms stay right', 'Spoken back out loud'],
      cta: 'Try the Voice Translator',
      link: '/translator'
    },
    {
      id: 'shopping-lists',
      title: 'Smart Shopping Lists',
      subtitle: 'Materials Picked Up Right, Every Run',
      description: 'Describe the job and AI drafts the full materials list. Send it to a worker — they check items off at the store, log prices, and snap the receipt. You see it all live.',
      icon: ShoppingCart,
      color: 'bg-teal-600',
      features: ['AI-drafted material lists', 'Live check-off at the store', 'Actual spend vs. estimate', 'Receipt photo proof'],
      cta: 'Build a Shopping List',
      link: '/become-a-contractor'
    },
    {
      id: 'ai-scheduler',
      title: 'AI Scheduling Engine',
      subtitle: 'Plan Jobs & Book Crews Automatically',
      description: 'Describe any job and AI builds a phase-by-phase schedule, then matches your best available crew to each phase. Delays cascade automatically and everyone stays notified.',
      icon: CalendarCheck,
      color: 'bg-[#EA580C]',
      features: ['AI phase-by-phase plans', 'Smart crew matching', 'Availability-aware booking', 'Auto-cascade delays'],
      cta: 'Open AI Scheduler',
      link: '/ai-scheduler'
    },
    {
      id: 'leads-marketplace',
      title: 'Leads Marketplace',
      subtitle: "Find Your Next Client",
      description: 'Browse verified homeowner leads in your area. Unlock contact info with credits and close more deals.',
      icon: ShoppingCart,
      color: 'bg-[#D95800]',
      features: ['Real homeowner project requests', 'Instant contact unlock', 'Up to 40% bonus credits', 'Filter by location'],
      cta: 'Browse Leads',
      link: '/leads-marketplace'
    },
    {
      id: 'voice-messaging',
      title: 'Voice Messaging',
      subtitle: "Talk, Don't Type",
      description: 'Record and send voice messages or convert speech to text instantly with AI-powered transcription.',
      icon: Microphone,
      color: 'bg-[#2B4C6F]',
      features: ['One-tap recording', 'Voice playback', 'Speech-to-text', 'Read receipts'],
      cta: 'Try Voice Messaging',
      link: '/messages'
    },
    {
      id: 'invoice-generator',
      title: 'Invoice Generator',
      subtitle: 'Get Paid Faster',
      description: 'Create professional invoices in seconds with automatic calculations, PDF export, and payment tracking.',
      icon: FileText,
      color: 'bg-emerald-600',
      features: ['One-click creation', 'PDF export', 'Email delivery', 'Payment tracking'],
      cta: 'Create Invoice',
      link: '/invoices'
    },
    {
      id: 'translation',
      title: 'Auto Translation',
      subtitle: 'Break Language Barriers',
      description: 'Translate messages instantly to communicate seamlessly with workers and contractors worldwide.',
      icon: Translate,
      color: 'bg-amber-500',
      features: ['12 languages', 'One-click translate', 'Your preferred language', 'In-chat integration'],
      cta: 'Start Messaging',
      link: '/messages'
    }
  ];

  useEffect(() => {
    setPageMeta({
      title: 'Construction Management Tools — Free for Contractors & Homeowners | ConstructHelp',
      description: 'Voice translator for job sites (12 languages), AI crew scheduling, smart shopping lists, leads marketplace, escrow payments and more — free construction tools built into ConstructHelp.',
      url: window.location.href,
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % newFeatures.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [newFeatures.length]);

  const nextFeature = () => {
    setActiveFeature((prev) => (prev + 1) % newFeatures.length);
  };

  const prevFeature = () => {
    setActiveFeature((prev) => (prev - 1 + newFeatures.length) % newFeatures.length);
  };

  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -50) nextFeature();
    else if (dx > 50) prevFeature();
    touchStartX.current = null;
  };

  const currentFeature = newFeatures[activeFeature];
  const FeatureIcon = currentFeature.icon;

  return (
    <div className="bg-[#F0F0F2] dark:bg-neutral-900">
      {/* Compact Tour Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]" data-testid="tour-hero">
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <div className="inline-flex items-center gap-2 bg-[#D95800] text-black text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-white shadow-[3px_3px_0px_#fff] mb-4">
            <Sparkle size={14} weight="fill" /> Platform Feature Tour
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight mb-3">
            Every tool. <span className="text-[#D95800]">One platform.</span>
          </h1>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            A deep dive into everything inside ConstructHelp — AI pricing, worker teams, client portals, escrow payments, and more.
          </p>
        </div>
      </div>

      {/* What's New - Feature Carousel */}
      <section className="py-12 bg-neutral-900 dark:bg-neutral-950 relative overflow-hidden" data-testid="whats-new-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section header */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center gap-2 bg-[#2B4C6F] text-white rounded-full px-4 py-1.5 border-2 border-black shadow-[2px_2px_0px_#000]">
              <Sparkle className="h-4 w-4" weight="bold" />
              <span className="text-sm font-bold uppercase tracking-wider">What's New</span>
            </div>
          </div>

          {/* Feature carousel */}
          <div className="flex items-center gap-6">
            {/* Previous button */}
            <button
              onClick={prevFeature}
              className="hidden md:flex w-14 h-14 rounded-xl bg-white/10 hover:bg-white/20 items-center justify-center text-white transition-transform active:translate-y-1 border-2 border-white/20 flex-shrink-0"
              data-testid="prev-feature-btn"
            >
              <CaretLeft className="h-6 w-6" weight="bold" />
            </button>

            {/* Main feature card */}
            <div className="flex-1 relative touch-pan-y" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-testid="feature-carousel-card">
              <div className={`${currentFeature.color} rounded-xl p-8 md:p-10 text-white border-2 border-black shadow-[6px_6px_0px_#000] transition-all duration-500`}>
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Left side - Content */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                        <FeatureIcon className="h-7 w-7" weight="bold" />
                      </div>
                      <div>
                        <h3 className="text-2xl md:text-3xl font-bold">{currentFeature.title}</h3>
                        <p className="text-white/80">{currentFeature.subtitle}</p>
                      </div>
                    </div>
                    
                    <p className="text-lg text-white/90 mb-6">
                      {currentFeature.description}
                    </p>
                    
                    <Button
                      onClick={() => currentFeature.link.startsWith('http')
                        ? window.open(currentFeature.link, '_blank', 'noopener')
                        : navigate(currentFeature.link)}
                      size="lg"
                      className="bg-white text-neutral-900 hover:bg-neutral-100 font-bold border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all h-14"
                      data-testid={`feature-cta-${currentFeature.id}`}
                    >
                      {currentFeature.cta}
                      <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
                    </Button>
                  </div>

                  {/* Right side - Feature pills */}
                  <div className="hidden md:block">
                    <div className="grid grid-cols-2 gap-3">
                      {currentFeature.features.map((feature, idx) => (
                        <div
                          key={idx}
                          className="bg-white/20 border-2 border-white/30 rounded-xl px-4 py-3 flex items-center gap-2"
                        >
                          <CheckCircle className="h-5 w-5 text-white" weight="bold" />
                          <span className="font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next button */}
            <button
              onClick={nextFeature}
              className="hidden md:flex w-14 h-14 rounded-xl bg-white/10 hover:bg-white/20 items-center justify-center text-white transition-transform active:translate-y-1 border-2 border-white/20 flex-shrink-0"
              data-testid="next-feature-btn"
            >
              <CaretRight className="h-6 w-6" weight="bold" />
            </button>
          </div>

          {/* Feature indicators */}
          <div className="flex items-center justify-center gap-4 mt-8">
            {newFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <button
                  key={feature.id}
                  onClick={() => setActiveFeature(idx)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all border-2 ${
                    idx === activeFeature 
                      ? 'bg-white text-neutral-900 border-black shadow-[2px_2px_0px_#000]' 
                      : 'bg-white/10 text-white/70 border-white/20 hover:bg-white/20'
                  }`}
                  data-testid={`feature-indicator-${feature.id}`}
                >
                  <Icon className="h-4 w-4" weight="bold" />
                  <span className="text-sm font-medium hidden sm:inline">{feature.title}</span>
                </button>
              );
            })}
          </div>

          {/* See all updates link */}
          <div className="flex justify-center mt-6">
            <button
              onClick={() => navigate('/timeline')}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-bold uppercase tracking-wider transition-colors border-b-2 border-white/30 hover:border-white pb-0.5"
              data-testid="see-all-updates-link"
            >
              See all updates <ArrowRight className="h-4 w-4" weight="bold" />
            </button>
          </div>
        </div>
      </section>

      <Features />

      {/* AI Job Pricing Tool Feature - Enhanced */}
      <section className="py-24 bg-neutral-900 text-white overflow-hidden relative" data-testid="ai-pricing-promo-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 51px), repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 51px)' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border-2 border-amber-500 px-4 py-2 mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-400"></span>
              </span>
              <span className="text-sm font-bold text-amber-400 uppercase tracking-widest">AI-POWERED</span>
            </div>
            
            <h2 className="text-4xl lg:text-6xl font-black mb-6 uppercase tracking-tight">
              Smart Job<br/>
              <span className="text-amber-500">Pricing Tool</span>
            </h2>
            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
              Material lists, tiered quotes, and instant estimates. Everything you need to price jobs like a pro.
            </p>
          </div>

          {/* Three Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Feature 1: Material Calculator */}
            <div className="bg-neutral-800 border-2 border-white p-6 shadow-[4px_4px_0px_#D95800]">
              <div className="w-14 h-14 bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-emerald-400" weight="bold" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Material Calculator</h3>
              <p className="text-neutral-400 text-sm mb-4">
                Get exact quantities - paint gallons, lumber feet, tile boxes. No more guessing on supply runs.
              </p>
              <div className="bg-neutral-900 border border-neutral-700 p-3 font-mono text-xs">
                <div className="flex justify-between text-neutral-500 mb-2 border-b border-neutral-700 pb-2">
                  <span>ITEM</span>
                  <span>QTY</span>
                </div>
                <div className="space-y-1 text-neutral-300">
                  <div className="flex justify-between"><span>Interior Paint</span><span className="text-emerald-400">6 gal</span></div>
                  <div className="flex justify-between"><span>Primer</span><span className="text-emerald-400">2 gal</span></div>
                  <div className="flex justify-between"><span>Drop Cloths</span><span className="text-emerald-400">4 pcs</span></div>
                  <div className="flex justify-between"><span>Painter's Tape</span><span className="text-emerald-400">8 rolls</span></div>
                </div>
              </div>
            </div>

            {/* Feature 2: Tiered Pricing */}
            <div className="bg-neutral-800 border-2 border-white p-6 shadow-[4px_4px_0px_#D95800]">
              <div className="w-14 h-14 bg-amber-500/20 border-2 border-amber-500 flex items-center justify-center mb-4">
                <Star className="w-7 h-7 text-amber-400" weight="fill" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Good / Better / Best</h3>
              <p className="text-neutral-400 text-sm mb-4">
                Three pricing tiers for every job. Give customers options and close more deals.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-neutral-700/50 border border-neutral-600">
                  <span className="text-neutral-400 text-sm">Economy</span>
                  <span className="font-mono font-bold">$2,500</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-amber-500/20 border-2 border-amber-500">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-400 text-sm font-bold">Standard</span>
                    <span className="text-[10px] bg-amber-500 text-black px-1 font-bold">BEST</span>
                  </div>
                  <span className="font-mono font-bold text-amber-400">$3,600</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-neutral-700/50 border border-neutral-600">
                  <span className="text-neutral-400 text-sm">Premium</span>
                  <span className="font-mono font-bold">$5,200</span>
                </div>
              </div>
            </div>

            {/* Feature 3: Save & Compare */}
            <div className="bg-neutral-800 border-2 border-white p-6 shadow-[4px_4px_0px_#D95800]">
              <div className="w-14 h-14 bg-[#2B4C6F]/20 border-2 border-[#2B4C6F] flex items-center justify-center mb-4">
                <Scales className="w-7 h-7 text-[#2B4C6F]" weight="bold" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Save & Compare</h3>
              <p className="text-neutral-400 text-sm mb-4">
                Bookmark estimates and compare up to 4 side-by-side. Track which quotes win jobs.
              </p>
              <div className="bg-neutral-900 border border-neutral-700 p-3">
                <div className="flex items-center justify-between text-xs mb-3 pb-2 border-b border-neutral-700">
                  <span className="text-neutral-500">COMPARING 3 ESTIMATES</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-neutral-800 p-2 border border-neutral-700">
                    <p className="text-[10px] text-neutral-500">Kitchen A</p>
                    <p className="font-mono text-sm text-[#2B4C6F]">$12k</p>
                  </div>
                  <div className="bg-emerald-500/20 p-2 border-2 border-emerald-500">
                    <p className="text-[10px] text-emerald-400">Kitchen B</p>
                    <p className="font-mono text-sm text-emerald-400">$9k</p>
                  </div>
                  <div className="bg-neutral-800 p-2 border border-neutral-700">
                    <p className="text-[10px] text-neutral-500">Kitchen C</p>
                    <p className="font-mono text-sm text-[#2B4C6F]">$15k</p>
                  </div>
                </div>
                <p className="text-[10px] text-emerald-400 mt-2 text-center">Kitchen B is lowest</p>
              </div>
            </div>
          </div>

          {/* Bottom Row: Additional Features + CTA */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Additional Features */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: UsersThree, title: 'Labor Breakdown', desc: 'Hours, crew size & skill level' },
                { icon: ShieldCheck, title: 'Permit Checker', desc: 'Know if permits are needed' },
                { icon: PaperPlaneTilt, title: 'Send Quotes', desc: 'Email PDF quotes to customers' },
                { icon: Clock, title: 'Timeline by Tier', desc: 'Different timelines per option' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-neutral-800/50 border border-neutral-700">
                  <item.icon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" weight="bold" />
                  <div>
                    <p className="font-bold text-sm">{item.title}</p>
                    <p className="text-xs text-neutral-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-8 border-2 border-black shadow-[6px_6px_0px_#000]">
              <h3 className="text-2xl font-black mb-4 uppercase">
                Stop Guessing.<br/>Start Pricing.
              </h3>
              <p className="text-white/90 mb-6">
                Use AI to create accurate, professional quotes in seconds. Stay competitive with data-driven pricing.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/ai-pricing')}
                  size="lg" 
                  className="bg-white text-orange-600 hover:bg-orange-50 font-black px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all uppercase tracking-wide"
                  data-testid="ai-pricing-cta-btn"
                >
                  Try AI Pricing
                  <Sparkle className="ml-2 h-5 w-5" weight="fill" />
                </Button>
                <Button 
                  onClick={() => navigate('/pricing')}
                  size="lg" 
                  className="bg-orange-800/50 text-white hover:bg-orange-800/70 font-bold px-6 h-14 border-2 border-white/30 uppercase tracking-wide"
                >
                  View Plans
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Scheduling Engine Feature */}
      <section className="py-24 bg-[#0F172A] text-white overflow-hidden relative" data-testid="ai-scheduler-promo-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 51px), repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 51px)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#EA580C]/20 border-2 border-[#EA580C] px-4 py-2 mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#EA580C]"></span>
              </span>
              <span className="text-sm font-bold text-[#EA580C] uppercase tracking-widest">New — AI-Powered</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-black mb-6 uppercase tracking-tight">
              AI Scheduling<br/>
              <span className="text-[#EA580C]">Engine</span>
            </h2>
            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
              Describe the job — AI builds the phase plan, books the right crew, and keeps everyone on schedule.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-neutral-800 border-2 border-white p-6 shadow-[4px_4px_0px_#EA580C]">
              <div className="w-14 h-14 bg-[#EA580C]/20 border-2 border-[#EA580C] flex items-center justify-center mb-4">
                <CalendarCheck className="w-7 h-7 text-[#EA580C]" weight="bold" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Phase-by-Phase Plans</h3>
              <p className="text-neutral-400 text-sm">Type a job description and get a realistic construction sequence — durations, dependencies, trades needed, and dates with Sundays skipped.</p>
            </div>
            <div className="bg-neutral-800 border-2 border-white p-6 shadow-[4px_4px_0px_#EA580C]">
              <div className="w-14 h-14 bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mb-4">
                <UsersThree className="w-7 h-7 text-emerald-400" weight="bold" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Smart Crew Matching</h3>
              <p className="text-neutral-400 text-sm">AI ranks your best workers for every phase — skills, ratings, and real availability calendars. One tap to assign, they're notified instantly.</p>
            </div>
            <div className="bg-neutral-800 border-2 border-white p-6 shadow-[4px_4px_0px_#EA580C]">
              <div className="w-14 h-14 bg-amber-500/20 border-2 border-amber-500 flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-amber-400" weight="bold" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Delays Auto-Cascade</h3>
              <p className="text-neutral-400 text-sm">A phase slips? Report it once — every downstream phase reschedules automatically and affected crews get re-notified. Plus Monday digests.</p>
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/ai-scheduler')}
              size="lg"
              className="bg-[#EA580C] text-white hover:bg-[#C2410C] font-black px-10 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all uppercase tracking-wide"
              data-testid="ai-scheduler-cta-btn"
            >
              Try the AI Scheduler
              <Sparkle className="ml-2 h-5 w-5" weight="fill" />
            </Button>
          </div>
        </div>
      </section>

      {/* Smart Shopping Lists Feature */}
      <ShoppingListPromo role="contractor" />

      {/* Voice Translator Feature */}
      <section className="py-24 bg-emerald-700 text-white overflow-hidden relative" data-testid="voice-translator-promo-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.15) 40px, rgba(255,255,255,0.15) 41px)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/15 border-2 border-white px-4 py-2 mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </span>
              <span className="text-sm font-bold uppercase tracking-widest">New — Voice AI</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-black mb-6 uppercase tracking-tight">
              Speak Your Language.<br/>
              <span className="text-emerald-200">They Hear Theirs.</span>
            </h2>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
              Tap the mic, talk, and hear the translation out loud in seconds — any pair of 12 languages, right on the job site.
            </p>
          </div>

          <div className="mb-16 grid md:grid-cols-2 gap-10 items-center">
            <div className="flex justify-center">
              <div className="relative w-full max-w-[320px] rounded-[2rem] border-4 border-black bg-black shadow-[8px_8px_0px_#022C22] overflow-hidden">
                <video
                  src="https://customer-assets-7cd3h4nn.emergentagent.net/job_58284b88-53d8-4c2d-a757-f70994c6780a/artifacts/pnkaonly_My%20movie%2056.mp4"
                  onPlay={() => trackVideoEvent('features-promo', 'play')}
                  onEnded={() => trackVideoEvent('features-promo', 'complete')}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full aspect-[9/16] object-cover"
                  data-testid="voice-translator-promo-video"
                />
              </div>
            </div>
            <div>
              <VoiceTranslatorMock />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-emerald-900/60 border-2 border-white p-6 shadow-[4px_4px_0px_#022C22]">
              <div className="w-14 h-14 bg-white/15 border-2 border-white flex items-center justify-center mb-4">
                <Microphone className="w-7 h-7 text-white" weight="fill" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Talk, Don't Type</h3>
              <p className="text-emerald-100 text-sm">One big mic button. Speak naturally in any of 12 languages — English, Spanish, Portuguese, Polish, Mandarin & more. Whisper AI transcribes perfectly even over job-site noise.</p>
            </div>
            <div className="bg-emerald-900/60 border-2 border-white p-6 shadow-[4px_4px_0px_#022C22]">
              <div className="w-14 h-14 bg-white/15 border-2 border-white flex items-center justify-center mb-4">
                <Wrench className="w-7 h-7 text-white" weight="bold" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Trade Terms Stay Right</h3>
              <p className="text-emerald-100 text-sm">Construction jargon translates correctly — rebar ↔ varilla, drywall ↔ tablaroca, joist ↔ viga, scaffold ↔ andamio. No embarrassing mix-ups.</p>
            </div>
            <div className="bg-emerald-900/60 border-2 border-white p-6 shadow-[4px_4px_0px_#022C22]">
              <div className="w-14 h-14 bg-white/15 border-2 border-white flex items-center justify-center mb-4">
                <SpeakerHigh className="w-7 h-7 text-white" weight="fill" />
              </div>
              <h3 className="text-xl font-black mb-2 uppercase">Spoken Back Out Loud</h3>
              <p className="text-emerald-100 text-sm">The translation plays as natural speech — hand your phone over or hold it up. Full conversation history with replay.</p>
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/translator')}
              size="lg"
              className="bg-white text-emerald-700 hover:bg-emerald-50 font-black px-10 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all uppercase tracking-wide"
              data-testid="voice-translator-cta-btn"
            >
              Try the Voice Translator
              <Microphone className="ml-2 h-5 w-5" weight="fill" />
            </Button>
            <p className="text-emerald-200 text-sm mt-3">Free with every contractor & worker account</p>
          </div>
        </div>
      </section>

      {/* Worker Teams Feature */}
      <section className="py-24 bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 text-white overflow-hidden relative" data-testid="worker-teams-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Mockup - LEFT */}
            <div className="relative order-2 md:order-1">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <UsersThree className="w-6 h-6 text-purple-600" weight="bold" />
                    <span className="font-bold text-xl">Worker Teams</span>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Public
                  </span>
                </div>
                
                {/* Team Card */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border-2 border-purple-200 mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center border-2 border-purple-300">
                      <UsersThree className="w-7 h-7 text-purple-600" weight="bold" />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-lg">Elite Electrical Crew</p>
                      <div className="flex items-center gap-3 text-sm text-neutral-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> 4 members
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Los Angeles
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="w-4 h-4" weight="fill" />
                      <span className="font-bold">4.9</span>
                    </div>
                  </div>
                </div>
                
                {/* Team Members Preview */}
                <p className="text-sm font-bold text-neutral-500 mb-3">Team Members</p>
                <div className="space-y-2 mb-4">
                  {[
                    { name: 'Mike Rodriguez', role: 'Team Leader', rate: '$75/hr', isLeader: true },
                    { name: 'Sarah Chen', role: 'Electrician', rate: '$65/hr', isLeader: false },
                    { name: 'Carlos Mendez', role: 'Electrician', rate: '$60/hr', isLeader: false },
                  ].map((member, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${member.isLeader ? 'bg-amber-100 border-2 border-amber-300' : 'bg-purple-100 border-2 border-purple-200'}`}>
                          {member.isLeader ? <Crown className="w-4 h-4 text-amber-600" weight="fill" /> : <Users className="w-4 h-4 text-purple-600" weight="bold" />}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{member.name}</p>
                          <p className="text-xs text-neutral-500">{member.role}</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-green-600">{member.rate}</span>
                    </div>
                  ))}
                </div>
                
                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {['Electrical', 'Wiring', 'Panel Install', 'Lighting'].map((skill, idx) => (
                    <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
                
                {/* CTA */}
                <button className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
                  <Briefcase className="w-5 h-5" weight="bold" />
                  Hire This Team
                </button>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -left-4 bg-amber-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-6">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Crown className="w-4 h-4" weight="fill" /> Team Leader
                </span>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-6">
                <span className="font-bold text-sm flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" weight="bold" /> 47 Jobs Done
                </span>
              </div>
            </div>
            
            {/* Content - RIGHT */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-300"></span>
                </span>
                <span className="text-sm font-semibold">TEAM COLLABORATION</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Hire Complete<br/>
                <span className="text-purple-200">Worker Teams</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Workers can form teams with colleagues they trust. Contractors can browse and hire entire crews in one click. Perfect for larger projects requiring multiple skilled hands.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Crown, title: 'Leader-Controlled Teams', desc: 'One person manages invites, applications & team profile' },
                  { icon: UsersThree, title: 'Up to 10 Members', desc: 'Build crews of any size with diverse skill sets' },
                  { icon: CurrencyDollar, title: 'Flexible Pricing', desc: 'Show individual rates or set a single team rate' },
                  { icon: Eye, title: 'Public or Private', desc: 'Get discovered or stay exclusive to your network' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/signup?type=worker')}
                  size="lg" 
                  className="bg-white text-purple-700 hover:bg-purple-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="worker-teams-worker-cta-btn"
                >
                  Create Your Team
                  <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
                </Button>
                <Button 
                  onClick={() => navigate('/browse-worker-teams')}
                  size="lg" 
                  className="bg-purple-800/50 text-white hover:bg-purple-800/70 font-bold px-8 h-14 border-2 border-white/30"
                  data-testid="worker-teams-contractor-cta-btn"
                >
                  Browse Teams
                  <UsersThree className="ml-2 h-5 w-5" weight="bold" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Portal Feature */}
      <section className="py-24 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white overflow-hidden relative" data-testid="client-portal-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Content - LEFT */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-300"></span>
                </span>
                <span className="text-sm font-semibold">HOMEOWNER ACCESS</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Keep Your<br/>
                <span className="text-amber-200">Clients Informed</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Give homeowners their own portal to track progress, view photos, approve milestones, and communicate directly. Build trust and get faster approvals.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Eye, title: 'Real-Time Visibility', desc: 'Clients see progress, photos, and documents instantly' },
                  { icon: ThumbsUp, title: 'Milestone Approvals', desc: 'Get client sign-off directly in the app' },
                  { icon: ChatCircle, title: 'Direct Messaging', desc: 'Two-way communication with change requests' },
                  { icon: Star, title: 'Reviews & Ratings', desc: 'Collect testimonials when the job is done' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/signup')}
                  size="lg" 
                  className="bg-white text-orange-700 hover:bg-orange-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="client-portal-cta-btn"
                >
                  Impress Your Clients
                  <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
                </Button>
                <div className="flex items-center gap-2 text-white/80">
                  <Envelope className="w-5 h-5" weight="fill" />
                  <span className="text-sm font-medium">One-click invite via email</span>
                </div>
              </div>
            </div>
            
            {/* Portal Mockup - RIGHT */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Handshake className="w-6 h-6 text-orange-600" weight="bold" />
                    <span className="font-bold text-xl">Client Portal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-orange-700">JD</span>
                    </div>
                    <span className="font-medium text-sm">John Doe</span>
                  </div>
                </div>
                
                {/* Project Header */}
                <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-black text-lg">Kitchen Renovation</p>
                      <p className="text-sm text-neutral-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> 123 Main St, Austin TX
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">In Progress</span>
                  </div>
                </div>
                
                {/* Tabs */}
                <div className="flex gap-1 mb-4 overflow-x-auto">
                  {['Overview', 'Timeline', 'Photos', 'Messages'].map((tab, idx) => (
                    <button
                      key={tab}
                      className={`px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap ${
                        idx === 0 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-neutral-100 text-neutral-600'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                
                {/* Milestone Preview */}
                <div className="space-y-3">
                  <p className="text-sm font-bold text-neutral-500">Active Milestones</p>
                  {[
                    { name: 'Cabinet Installation', progress: 100, status: 'Pending Approval', amount: '$3,500' },
                    { name: 'Countertop Fitting', progress: 60, status: 'In Progress', amount: '$2,800' },
                  ].map((milestone, idx) => (
                    <div key={idx} className="p-3 border-2 border-neutral-200 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm">{milestone.name}</span>
                        <span className="text-xs font-bold text-orange-600">{milestone.amount}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${milestone.progress === 100 ? 'bg-green-500' : 'bg-orange-500'}`}
                            style={{ width: `${milestone.progress}%` }}
                          />
                        </div>
                        <span className={`text-xs font-bold ${milestone.progress === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                          {milestone.status}
                        </span>
                      </div>
                      {milestone.progress === 100 && (
                        <button className="mt-2 w-full py-2 bg-green-500 text-white text-xs font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000]">
                          Approve Milestone
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-green-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" weight="bold" /> Approved
                </span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-orange-600 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-6">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Bell className="w-4 h-4" weight="bold" /> Live Updates
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      
      <ProjectManagement />
      
      <WorkerProfiles />
      
      {/* Pricing CTA Section */}
      <section className="py-24 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white relative overflow-hidden" data-testid="pricing-cta-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(91, 143, 185, 0.3) 10px,
              rgba(91, 143, 185, 0.3) 20px
            )`
          }} />
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-[#2B4C6F] rounded-full px-4 py-2 mb-6 border-2 border-white/20">
              <Star size={16} weight="fill" className="text-amber-300" />
              <span className="text-sm font-semibold">SIMPLE, TRANSPARENT PRICING</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              Plans That Fit Your Needs
            </h2>
            <p className="text-xl text-neutral-300 max-w-2xl mx-auto mb-8">
              Whether you're a worker looking for opportunities or a contractor building your crew, 
              we have a plan that's right for you. No hidden fees, cancel anytime.
            </p>
            
            {/* Quick Price Preview */}
            <div className="grid md:grid-cols-3 gap-6 mb-10 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl p-6">
                <p className="text-neutral-400 text-sm font-bold mb-1">WORKERS START AT</p>
                <p className="text-4xl font-black">$0<span className="text-lg font-normal text-neutral-400">/mo</span></p>
                <p className="text-sm text-neutral-400 mt-1">Free forever plan</p>
              </div>
              <div className="bg-[#2B4C6F]/30 backdrop-blur-sm border-2 border-[#2B4C6F] rounded-xl p-6 scale-105">
                <p className="text-[#2B4C6F] text-sm font-bold mb-1">MOST POPULAR</p>
                <p className="text-4xl font-black">$19.99<span className="text-lg font-normal text-neutral-400">/mo</span></p>
                <p className="text-sm text-neutral-400 mt-1">Pro Worker plan</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl p-6">
                <p className="text-neutral-400 text-sm font-bold mb-1">CONTRACTORS FROM</p>
                <p className="text-4xl font-black">$79<span className="text-lg font-normal text-neutral-400">/mo</span></p>
                <p className="text-sm text-neutral-400 mt-1">Business plan</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => navigate('/pricing')}
                size="lg" 
                className="bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="view-pricing-btn"
              >
                View All Plans & Pricing
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/signup')}
                variant="outline"
                size="lg" 
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-8 h-14"
              >
                Start Free Trial
              </Button>
            </div>
            
            <p className="mt-6 text-neutral-400 text-sm">
              14-day free trial on all paid plans • No credit card required
            </p>
          </div>
        </div>
      </section>
      
      {/* Voice Messaging Feature */}
      <section className="py-24 bg-neutral-900 text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#2B4C6F] rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Voice Messaging <br/>
                <span className="text-[#2B4C6F]">Made Simple</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Too busy to type? Record voice messages on the go and let your team hear exactly what you mean.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Microphone, title: 'One-Tap Recording', desc: "Press the mic button and start talking - it's that easy" },
                  { icon: SpeakerHigh, title: 'Send Voice Messages', desc: 'Recipients can play back your audio message anytime' },
                  { icon: ChatCircle, title: 'Voice-to-Text', desc: 'AI-powered transcription converts your voice to text instantly' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#2B4C6F] rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup')}
                size="lg" 
                className="bg-[#2B4C6F] text-white hover:bg-[#4A7DA6] font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="voice-messaging-cta-btn"
              >
                Try Voice Messaging
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
            
            {/* Voice message mockup */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b-2 border-neutral-200">
                  <div className="w-10 h-10 bg-[#2B4C6F] rounded-full flex items-center justify-center text-white font-bold">MS</div>
                  <div>
                    <p className="font-semibold text-neutral-900">Mike Stevens</p>
                    <p className="text-xs text-green-600 font-medium">Online</p>
                  </div>
                </div>
                
                {/* Voice message bubble */}
                <div className="space-y-4">
                  <div className="flex justify-start">
                    <div className="bg-neutral-100 rounded-xl rounded-bl-md px-4 py-3 max-w-[85%] border-2 border-neutral-200">
                      <div className="flex items-center gap-3">
                        <button className="w-10 h-10 bg-[#2B4C6F] hover:bg-[#4A7DA6] rounded-full flex items-center justify-center text-white transition-colors border-2 border-black">
                          <Play className="h-4 w-4 ml-0.5" weight="fill" />
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-0.5 h-8">
                            {[40, 60, 80, 50, 90, 70, 45, 85, 55, 75, 65, 45, 80, 60, 40].map((h, i) => (
                              <div key={i} className="w-1 bg-[#2B4C6F]/50 rounded-full" style={{ height: `${h}%` }} />
                            ))}
                          </div>
                          <div className="flex justify-between text-xs text-neutral-500 mt-1">
                            <span>0:00</span>
                            <span>0:12</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-[#2B4C6F] flex items-center gap-1 font-medium">
                          <Microphone className="h-3 w-3" weight="bold" />
                          Voice
                        </span>
                        <span className="text-xs text-neutral-400">2:34 PM</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <div className="bg-[#2B4C6F] text-white rounded-xl rounded-br-md px-4 py-3 max-w-[80%] border-2 border-black">
                      <p>Got it! I'll be there at 8am sharp.</p>
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className="text-xs text-white/70">2:35 PM</span>
                        <CheckCircle className="h-3 w-3 text-white/70" weight="fill" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-[#2B4C6F] text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Headphones className="h-4 w-4" weight="bold" />
                  Playback!
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invoice Generator Feature */}
      <section className="py-24 bg-emerald-600 text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Invoice mockup - LEFT side */}
            <div className="order-2 md:order-1 relative">
              <div className="bg-white rounded-xl overflow-hidden border-2 border-black shadow-[6px_6px_0px_#000]">
                <div className="bg-emerald-600 text-white p-6 border-b-2 border-black">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-bold">INVOICE</h3>
                      <p className="text-emerald-200 text-sm mt-1">INV-2026-0042</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-emerald-200">Date</p>
                      <p className="font-semibold">March 28, 2026</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 text-neutral-900">
                  <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                    <div>
                      <p className="text-emerald-600 font-bold text-xs uppercase">From</p>
                      <p className="font-semibold">Mike Rodriguez</p>
                      <p className="text-neutral-500">Skilled Carpenter</p>
                    </div>
                    <div>
                      <p className="text-emerald-600 font-bold text-xs uppercase">Bill To</p>
                      <p className="font-semibold">ABC Construction</p>
                      <p className="text-neutral-500">contractor@example.com</p>
                    </div>
                  </div>
                  
                  <div className="border-t-2 border-b-2 border-neutral-200 py-4 mb-4">
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-4">
                        <span className="col-span-2">Kitchen Cabinet Installation</span>
                        <span className="text-right text-neutral-500">8 hrs</span>
                        <span className="text-right font-medium">$480.00</span>
                      </div>
                      <div className="grid grid-cols-4">
                        <span className="col-span-2">Custom Shelving Build</span>
                        <span className="text-right text-neutral-500">4 hrs</span>
                        <span className="text-right font-medium">$240.00</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <div className="w-48 space-y-1 text-sm">
                      <div className="flex justify-between pt-2 border-t-2 border-neutral-200 font-bold text-lg">
                        <span>Total</span>
                        <span className="text-emerald-600">$912.60</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -left-4 bg-white text-emerald-600 rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Receipt className="h-4 w-4" weight="bold" />
                  Professional
                </span>
              </div>
            </div>
            
            {/* Content - RIGHT side */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Professional Invoicing <br/>
                <span className="text-emerald-200">Made Easy</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Create polished invoices in seconds. Track payments, download PDFs, and get paid faster.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: FileText, title: 'One-Click Creation', desc: 'Add line items, set tax rates, and generate invoices instantly' },
                  { icon: PaperPlaneTilt, title: 'Send & Track', desc: 'Email invoices directly and track payment status in real-time' },
                  { icon: DownloadSimple, title: 'PDF Export', desc: 'Download professional PDF invoices for your records' },
                  { icon: Calculator, title: 'Auto-Calculations', desc: 'Subtotals, taxes, and totals calculated automatically' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup')}
                size="lg" 
                className="bg-white text-emerald-600 hover:bg-neutral-100 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="invoice-feature-cta-btn"
              >
                Start Invoicing
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Translation Feature */}
      <section className="py-20 bg-amber-500 text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Break the Language Barrier
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Communicate seamlessly with workers and contractors in 12 languages — Spanish, Portuguese, Polish, Mandarin, Vietnamese & more.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Translate, title: 'Instant Translation', desc: 'One-click translation to your preferred language on any message' },
                  { icon: Globe, title: 'Auto Language Detection', desc: 'Automatically detects the language and translates accordingly' },
                  { icon: ChatCircle, title: 'Built Into Chat', desc: 'No extra apps needed - translation is right in your messages' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup')}
                size="lg" 
                className="bg-white text-amber-600 hover:bg-neutral-100 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
              >
                Try It Free
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
            
            {/* Chat mockup */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b-2 border-neutral-200">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">JM</div>
                  <div>
                    <p className="font-semibold text-neutral-900">Juan Martinez</p>
                    <p className="text-xs text-green-600 font-medium">Online</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-start">
                    <div className="bg-neutral-100 rounded-xl rounded-bl-md px-4 py-3 max-w-[80%] border-2 border-neutral-200">
                      <p className="text-neutral-900">Cuando podemos empezar el trabajo?</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button className="text-xs text-[#2B4C6F] flex items-center gap-1 hover:underline font-medium">
                          <Translate className="h-3 w-3" weight="bold" />
                          Translate
                        </button>
                      </div>
                      <div className="mt-2 pt-2 border-t border-neutral-200">
                        <p className="text-xs text-neutral-500 flex items-center gap-1">
                          <Translate className="h-3 w-3" weight="bold" />
                          Translation:
                        </p>
                        <p className="text-neutral-700 italic text-sm">When can we start the work?</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <div className="bg-[#2B4C6F] text-white rounded-xl rounded-br-md px-4 py-3 max-w-[80%] border-2 border-black">
                      <p>We can start on Monday morning!</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm">FREE!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Planning Feature */}
      <section className="py-24 bg-neutral-900 text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Kanban Preview */}
            <div className="order-2 md:order-1">
              <div className="bg-white/10 border-2 border-white/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-lg">Kitchen Renovation</h4>
                  <span className="text-xs bg-[#2B4C6F] px-3 py-1 rounded-full border border-white/30">In Progress</span>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { title: 'To Do', color: 'bg-neutral-400', count: 3, tasks: ['Order cabinets', 'Schedule permits'] },
                    { title: 'In Progress', color: 'bg-[#2B4C6F]', count: 2, tasks: ['Install plumbing'] },
                    { title: 'Done', color: 'bg-emerald-500', count: 4, tasks: ['Demo old kitchen'] }
                  ].map((col, idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-2 h-2 rounded-full ${col.color}`}></div>
                        <span className="text-xs font-medium text-neutral-300">{col.title}</span>
                        <span className="text-xs bg-white/10 px-1.5 rounded">{col.count}</span>
                      </div>
                      <div className="space-y-2">
                        {col.tasks.map((task, i) => (
                          <div key={i} className={`bg-white/10 rounded p-2 border border-white/10 ${col.title === 'Done' ? 'opacity-70' : ''}`}>
                            <p className={`text-xs font-medium truncate ${col.title === 'Done' ? 'line-through' : ''}`}>{task}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-neutral-400">Progress</span>
                    <span className="font-medium">44%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div className="bg-[#2B4C6F] h-2 rounded-full" style={{width: '44%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-[#2B4C6F] rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Project Planning <br/>
                <span className="text-[#2B4C6F]">Kanban Board</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Organize every job with a drag-and-drop Kanban board. Track tasks, assign workers, and hit your deadlines.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: ClipboardText, title: 'Drag & Drop Tasks', desc: 'Move tasks between To Do, In Progress, Completed, and Blocked' },
                  { icon: Users, title: 'Assign Workers', desc: 'Assign tasks to your crew and they get notified instantly' },
                  { icon: CurrencyDollar, title: 'Milestones & Budgets', desc: 'Track project milestones and keep spending on target' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#2B4C6F] rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup?type=contractor')}
                size="lg" 
                className="bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="project-planning-cta-btn"
              >
                Start Planning Projects
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Feature */}
      <section className="py-24 bg-[#2B4C6F] text-white overflow-hidden relative" data-testid="calendar-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Never Miss a <br/>
                <span className="text-white/80">Deadline Again</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Keep track of all your jobs, tasks, milestones, and payment deadlines in one beautiful calendar view.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Briefcase, title: 'All Jobs in One View', desc: 'See job start dates, deadlines, and milestones at a glance' },
                  { icon: Bell, title: 'Custom Events', desc: 'Add meetings, site visits, and personal reminders' },
                  { icon: CurrencyDollar, title: 'Payment Tracking', desc: 'Never miss an escrow payment or invoice due date' },
                  { icon: ClipboardText, title: 'Task Due Dates', desc: 'Project tasks automatically appear on your calendar' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup')}
                size="lg" 
                className="bg-white text-[#2B4C6F] hover:bg-neutral-100 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="calendar-feature-cta-btn"
              >
                Start Using Calendar
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
            
            {/* Calendar mockup */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#2B4C6F] rounded-xl flex items-center justify-center border-2 border-black">
                      <CalendarBlank className="w-5 h-5 text-white" weight="bold" />
                    </div>
                    <div>
                      <p className="font-bold text-lg">March 2026</p>
                      <p className="text-xs text-neutral-500">4 events this week</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button className="p-2 hover:bg-neutral-100 rounded-lg border border-neutral-200">
                      <CaretLeft className="w-4 h-4 text-neutral-600" weight="bold" />
                    </button>
                    <button className="p-2 hover:bg-neutral-100 rounded-lg border border-neutral-200">
                      <CaretRight className="w-4 h-4 text-neutral-600" weight="bold" />
                    </button>
                  </div>
                </div>
                
                {/* Events List */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border-l-4 border-emerald-500">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-emerald-600" weight="bold" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Job Start: Kitchen Reno</p>
                      <p className="text-xs text-neutral-500">Mar 28, 8:00 AM</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border-l-4 border-amber-500">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <CurrencyDollar className="w-4 h-4 text-amber-600" weight="bold" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Payment Due: Phase 1</p>
                      <p className="text-xs text-neutral-500">Mar 30 - $5,000</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#2B4C6F]/10 rounded-xl border-l-4 border-[#2B4C6F]">
                    <div className="w-8 h-8 bg-[#2B4C6F]/20 rounded-lg flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#2B4C6F]" weight="bold" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Site Visit: Downtown</p>
                      <p className="text-xs text-neutral-500">Mar 30, 10:00 AM</p>
                    </div>
                  </div>
                </div>
                
                {/* Legend */}
                <div className="mt-4 pt-4 border-t flex flex-wrap gap-3 justify-center">
                  {[
                    { color: 'bg-emerald-500', label: 'Jobs' },
                    { color: 'bg-amber-500', label: 'Payments' },
                    { color: 'bg-[#2B4C6F]', label: 'Events' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${item.color}`}></div>
                      <span className="text-xs text-neutral-600">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="absolute -top-4 -left-4 bg-white text-[#2B4C6F] rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-12">
                <span className="font-bold text-sm">Sync All Events!</span>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-neutral-900 text-white rounded-full px-4 py-2 border-2 border-white/30 shadow-[2px_2px_0px_#2B4C6F] transform rotate-6">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" weight="bold" /> Never Late
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Documentation Feature */}
      <section className="py-24 bg-gradient-to-br from-purple-600 to-purple-800 text-white overflow-hidden relative" data-testid="photo-docs-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Content - LEFT */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Photo Documentation<br/>
                <span className="text-purple-200">Before & After</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Document every job with before, progress, and after photos. Build your portfolio automatically and provide visual proof for escrow milestones.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Camera, title: 'Capture Every Stage', desc: 'Before, progress, after, and milestone photos organized by type' },
                  { icon: Folder, title: 'Auto-Portfolio', desc: 'Workers can add job photos directly to their portfolio showcase' },
                  { icon: ShieldCheck, title: 'Milestone Proof', desc: 'Attach photos to escrow milestones as completion evidence' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup')}
                size="lg" 
                className="bg-white text-purple-700 hover:bg-purple-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="photo-docs-cta-btn"
              >
                Start Documenting Jobs
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
            
            {/* Photo Gallery Mockup - RIGHT */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-purple-600" weight="bold" />
                    <span className="font-bold text-lg">Photo Documentation</span>
                    <span className="text-xs bg-neutral-100 px-2 py-0.5 rounded-full text-neutral-600">(6)</span>
                  </div>
                  <button className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg border-2 border-black font-bold">
                    <Camera className="w-4 h-4" weight="bold" />
                    Add Photo
                  </button>
                </div>
                
                {/* Before Photos Section */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span className="text-sm font-bold text-neutral-700">Before Photos</span>
                    <span className="text-xs text-neutral-400">(2)</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="aspect-square bg-amber-100 rounded-lg border-2 border-amber-300 flex items-center justify-center overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-br from-amber-200 to-amber-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-amber-600 bg-amber-500/20 px-2 py-0.5 rounded">Before</span>
                      </div>
                    </div>
                    <div className="aspect-square bg-amber-100 rounded-lg border-2 border-amber-300 overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-br from-amber-300 to-amber-100"></div>
                    </div>
                  </div>
                </div>
                
                {/* Progress Photos Section */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-bold text-neutral-700">Progress Photos</span>
                    <span className="text-xs text-neutral-400">(2)</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="aspect-square bg-blue-100 rounded-lg border-2 border-blue-300 flex items-center justify-center overflow-hidden">
                      <span className="text-xs font-bold text-blue-600 bg-blue-500/20 px-2 py-0.5 rounded">50%</span>
                    </div>
                    <div className="aspect-square bg-blue-100 rounded-lg border-2 border-blue-300 overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-br from-blue-300 to-blue-100"></div>
                    </div>
                  </div>
                </div>
                
                {/* After Photos Section */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-sm font-bold text-neutral-700">After Photos</span>
                    <span className="text-xs text-neutral-400">(2)</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="aspect-square bg-emerald-100 rounded-lg border-2 border-emerald-300 flex items-center justify-center overflow-hidden">
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-500/20 px-2 py-0.5 rounded">Done!</span>
                    </div>
                    <div className="aspect-square bg-emerald-100 rounded-lg border-2 border-emerald-300 overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-br from-emerald-300 to-emerald-100"></div>
                    </div>
                  </div>
                </div>
                
                {/* Portfolio Callout */}
                <div className="mt-4 pt-4 border-t-2 border-neutral-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-purple-600" weight="bold" />
                    <span className="text-xs text-neutral-600">Auto-add to portfolio</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div className="w-8 h-4 bg-purple-600 rounded-full relative">
                      <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" weight="bold" /> Visual Proof
                </span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-amber-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-6">
                <span className="font-bold text-sm">Before & After!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Tracking Feature */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 to-teal-700 text-white overflow-hidden relative" data-testid="cost-tracking-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Content - LEFT */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Cost Tracking<br/>
                <span className="text-emerald-200">Stay on Budget</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Track every expense across your jobs. Compare actual spend vs estimates, see breakdowns by category, and never go over budget again.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: ChartPie, title: '6 Cost Categories', desc: 'Labor, Materials, Equipment, Permits, Subcontractor, Other' },
                  { icon: TrendUp, title: 'Budget Progress', desc: 'Visual progress bar with remaining amount and % used' },
                  { icon: Warning, title: 'Over-Budget Alerts', desc: 'Get warned instantly when spending exceeds estimates' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup')}
                size="lg" 
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="cost-tracking-cta-btn"
              >
                Start Tracking Costs
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
            
            {/* Cost Dashboard Mockup - RIGHT */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ChartPie className="w-5 h-5 text-emerald-600" weight="bold" />
                    <span className="font-bold text-lg">Cost Tracking</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-neutral-500">Total Spent</p>
                    <p className="text-2xl font-black text-neutral-900">$8,450</p>
                  </div>
                </div>
                
                {/* Budget Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-bold text-neutral-700">Budget Progress</span>
                    <span className="text-neutral-500">$10,000 budget</span>
                  </div>
                  <div className="h-4 bg-neutral-100 rounded-full overflow-hidden border-2 border-neutral-200">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" style={{width: '84.5%'}}></div>
                  </div>
                  <div className="flex justify-between mt-1 text-sm">
                    <span className="font-bold text-emerald-600">84.5% used</span>
                    <span className="text-neutral-500">$1,550 remaining</span>
                  </div>
                </div>
                
                {/* Category Breakdown */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <div className="flex items-center gap-1 mb-1">
                      <Users className="w-4 h-4 text-blue-500" weight="bold" />
                      <span className="text-xs font-bold text-blue-700">Labor</span>
                    </div>
                    <span className="text-lg font-black text-neutral-900">$4,200</span>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                    <div className="flex items-center gap-1 mb-1">
                      <Package className="w-4 h-4 text-emerald-500" weight="bold" />
                      <span className="text-xs font-bold text-emerald-700">Materials</span>
                    </div>
                    <span className="text-lg font-black text-neutral-900">$2,850</span>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg border-2 border-amber-200">
                    <div className="flex items-center gap-1 mb-1">
                      <Wrench className="w-4 h-4 text-amber-500" weight="bold" />
                      <span className="text-xs font-bold text-amber-700">Equipment</span>
                    </div>
                    <span className="text-lg font-black text-neutral-900">$1,400</span>
                  </div>
                </div>
                
                {/* Recent Costs */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-neutral-500 uppercase">Recent Costs</p>
                  <div className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg border border-neutral-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                        <Package className="w-4 h-4 text-white" weight="bold" />
                      </div>
                      <div>
                        <p className="text-sm font-bold">Lumber delivery</p>
                        <p className="text-xs text-neutral-500">Home Depot</p>
                      </div>
                    </div>
                    <span className="font-black">$850</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg border border-neutral-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-white" weight="bold" />
                      </div>
                      <div>
                        <p className="text-sm font-bold">Day labor - framing</p>
                        <p className="text-xs text-neutral-500">ABC Contractors</p>
                      </div>
                    </div>
                    <span className="font-black">$1,200</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <TrendUp className="w-4 h-4" weight="bold" /> On Track!
                </span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-amber-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-6">
                <span className="font-bold text-sm">$1,550 Left</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Time Tracking Feature */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-indigo-700 text-white overflow-hidden relative" data-testid="time-tracking-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Content - LEFT */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-400"></span>
                </span>
                <span className="text-sm font-semibold">NEW FEATURE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Time Tracking<br/>
                <span className="text-blue-200">For Hourly Jobs</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Workers clock in/out with GPS verification. Track breaks, calculate overtime automatically, and export professional timesheets for payroll.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: MapPin, title: 'GPS Verification', desc: 'Location recorded at clock-in for jobsite verification' },
                  { icon: Coffee, title: 'Break Tracking', desc: 'Track rest and lunch breaks with total work time calculation' },
                  { icon: TrendUp, title: 'Overtime Rules', desc: 'Custom daily/weekly thresholds with 1.5x and 2x multipliers' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/signup')}
                size="lg" 
                className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="time-tracking-cta-btn"
              >
                Start Tracking Time
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>
            
            {/* Time Clock Mockup - RIGHT */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Timer className="w-5 h-5 text-blue-600" weight="bold" />
                    <span className="font-bold text-lg">Time Clock</span>
                  </div>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-bold rounded-full border border-emerald-300">Active</span>
                </div>
                
                {/* Current Session */}
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-5 text-white mb-4 border-2 border-black">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/80">Currently Working</p>
                      <p className="text-lg font-bold">Kitchen Renovation</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black">4h 32m</p>
                      <p className="text-sm text-white/80">
                        <Coffee className="w-3 h-3 inline mr-1" />
                        30m break
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* GPS Verified Badge */}
                <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <MapPin className="w-5 h-5 text-blue-600" weight="bold" />
                  <span className="text-sm font-medium text-blue-700">GPS verified at 8:00 AM</span>
                  <CheckCircle className="w-4 h-4 text-emerald-500 ml-auto" weight="fill" />
                </div>
                
                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button className="flex items-center justify-center gap-2 p-3 bg-amber-100 rounded-lg border-2 border-amber-300 text-amber-700 font-bold">
                    <Coffee className="w-5 h-5" weight="bold" />
                    Take Break
                  </button>
                  <button className="flex items-center justify-center gap-2 p-3 bg-red-100 rounded-lg border-2 border-red-300 text-red-700 font-bold">
                    <Clock className="w-5 h-5" weight="bold" />
                    Clock Out
                  </button>
                </div>
                
                {/* Weekly Summary */}
                <div className="p-4 bg-neutral-50 rounded-lg border-2 border-neutral-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-neutral-700">This Week</span>
                    <button className="flex items-center gap-1 text-xs text-blue-600 font-bold">
                      <Export className="w-4 h-4" />
                      Export
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-black text-neutral-900">32.5</p>
                      <p className="text-xs text-neutral-500">Total Hours</p>
                    </div>
                    <div>
                      <p className="text-2xl font-black text-emerald-600">28.0</p>
                      <p className="text-xs text-neutral-500">Regular</p>
                    </div>
                    <div>
                      <p className="text-2xl font-black text-amber-600">4.5</p>
                      <p className="text-xs text-neutral-500">Overtime</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-blue-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Timer className="w-4 h-4" weight="bold" /> Live!
                </span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-amber-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-6">
                <span className="font-bold text-sm">4.5h OT</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Document Storage Feature */}
      <section className="py-24 bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-600 text-white overflow-hidden relative" data-testid="documents-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Content - LEFT */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-400"></span>
                </span>
                <span className="text-sm font-semibold">SECURE STORAGE</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                All Your Job<br/>
                <span className="text-purple-200">Documents in One Place</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Upload contracts, permits, insurance certificates, blueprints, and more. Keep every project organized with secure cloud storage and easy sharing with your team.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Upload, title: 'Easy Uploads', desc: 'Drag & drop any file type up to 10 MB' },
                  { icon: FolderOpen, title: '9 Categories', desc: 'Contracts, permits, blueprints, safety docs & more' },
                  { icon: Lock, title: 'Access Control', desc: 'Share specific docs with assigned workers' },
                  { icon: Cloud, title: 'Cloud Storage', desc: 'Secure, always available from any device' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/signup')}
                  size="lg" 
                  className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="documents-cta-btn"
                >
                  Get Organized
                  <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
                </Button>
                <div className="flex items-center gap-2 text-white/80">
                  <ShieldCheck className="w-5 h-5" weight="fill" />
                  <span className="text-sm font-medium">Encrypted & Secure</span>
                </div>
              </div>
            </div>
            
            {/* Document Mockup - RIGHT */}
            <div className="relative">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-6 h-6 text-indigo-600" weight="bold" />
                    <span className="font-bold text-xl">Job Documents</span>
                  </div>
                  <button className="px-4 py-2 bg-indigo-500 text-white text-sm font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] flex items-center gap-1">
                    <Upload className="w-4 h-4" weight="bold" />
                    Upload
                  </button>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="p-3 bg-indigo-50 rounded-xl border-2 border-indigo-200 text-center">
                    <p className="text-2xl font-black text-indigo-700">12</p>
                    <p className="text-xs text-indigo-600 font-medium">Files</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-xl border-2 border-purple-200 text-center">
                    <p className="text-2xl font-black text-purple-700">5</p>
                    <p className="text-xs text-purple-600 font-medium">Categories</p>
                  </div>
                  <div className="p-3 bg-violet-50 rounded-xl border-2 border-violet-200 text-center">
                    <p className="text-2xl font-black text-violet-700">24 MB</p>
                    <p className="text-xs text-violet-600 font-medium">Storage</p>
                  </div>
                </div>
                
                {/* Category List */}
                <div className="space-y-2">
                  <p className="text-sm font-bold text-neutral-500 mb-3">Document Categories</p>
                  {[
                    { icon: FileText, name: 'Contracts', count: 3, color: 'bg-blue-100 text-blue-700 border-blue-200' },
                    { icon: Certificate, name: 'Permits', count: 2, color: 'bg-green-100 text-green-700 border-green-200' },
                    { icon: ShieldCheck, name: 'Insurance', count: 2, color: 'bg-purple-100 text-purple-700 border-purple-200' },
                    { icon: Blueprint, name: 'Blueprints', count: 3, color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
                    { icon: Warning, name: 'Safety Docs', count: 2, color: 'bg-red-100 text-red-700 border-red-200' },
                  ].map((cat, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border-2 ${cat.color}`}>
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-5 h-5" weight="bold" />
                        <span className="font-bold">{cat.name}</span>
                      </div>
                      <span className="px-2 py-1 bg-white/50 rounded-full text-xs font-bold">{cat.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-indigo-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <FilePdf className="w-4 h-4" weight="bold" /> PDF
                </span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-purple-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-6">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Lock className="w-4 h-4" weight="bold" /> Secure
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Worker Wallet & Payout Feature */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white overflow-hidden relative" data-testid="wallet-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Wallet Mockup - LEFT */}
            <div className="relative order-2 md:order-1">
              <div className="bg-white rounded-xl p-6 text-neutral-900 border-2 border-black shadow-[6px_6px_0px_#000]">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-6 h-6 text-emerald-600" weight="bold" />
                    <span className="font-bold text-xl">My Wallet</span>
                  </div>
                  <button className="px-4 py-2 bg-emerald-500 text-white text-sm font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000]">
                    Withdraw
                  </button>
                </div>
                
                {/* Balance Cards */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="p-4 bg-emerald-50 rounded-xl border-2 border-emerald-200">
                    <p className="text-xs text-emerald-600 font-medium mb-1">Available</p>
                    <p className="text-2xl font-black text-emerald-700">$4,250</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl border-2 border-amber-200">
                    <p className="text-xs text-amber-600 font-medium mb-1">Pending</p>
                    <p className="text-2xl font-black text-amber-700">$850</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">Earned</p>
                    <p className="text-2xl font-black text-blue-700">$18.5k</p>
                  </div>
                </div>
                
                {/* Stripe Connect Banner */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl p-4 text-white mb-4 border-2 border-black">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Lightning className="w-8 h-8" weight="fill" />
                      <div>
                        <p className="font-bold">Instant Payouts</p>
                        <p className="text-sm text-white/80">Connected via Stripe</p>
                      </div>
                    </div>
                    <CheckCircle className="w-6 h-6 text-emerald-400" weight="fill" />
                  </div>
                </div>
                
                {/* Recent Transactions */}
                <div className="space-y-2">
                  <p className="text-sm font-bold text-neutral-500 mb-2">Recent Transactions</p>
                  {[
                    { desc: 'Kitchen Renovation Payment', amount: '+$1,500', type: 'credit' },
                    { desc: 'Instant Payout to Bank', amount: '-$2,000', type: 'debit' },
                    { desc: 'Bathroom Remodel Payment', amount: '+$2,750', type: 'credit' },
                  ].map((tx, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                      <div className="flex items-center gap-2">
                        {tx.type === 'credit' ? (
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                            <TrendUp className="w-4 h-4 text-emerald-600" weight="bold" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Bank className="w-4 h-4 text-blue-600" weight="bold" />
                          </div>
                        )}
                        <span className="text-sm font-medium">{tx.desc}</span>
                      </div>
                      <span className={`font-bold ${tx.type === 'credit' ? 'text-emerald-600' : 'text-blue-600'}`}>
                        {tx.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform rotate-12">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Coins className="w-4 h-4" weight="bold" /> $4,250
                </span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-indigo-500 text-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000] transform -rotate-6">
                <span className="font-bold text-sm flex items-center gap-1">
                  <Lightning className="w-4 h-4" weight="fill" /> Instant!
                </span>
              </div>
            </div>
            
            {/* Content - RIGHT */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400"></span>
                </span>
                <span className="text-sm font-semibold">SECURE PAYMENTS</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Get Paid<br/>
                <span className="text-emerald-200">Your Way</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                When jobs are completed and escrow is released, funds go directly to your wallet. Withdraw instantly to your bank, or wait for weekly payouts.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Lightning, title: 'Instant Payouts', desc: 'Get your money in minutes via Stripe Connect' },
                  { icon: Bank, title: 'Direct to Bank', desc: 'Standard payouts arrive in 2-3 business days' },
                  { icon: ShieldCheck, title: 'Escrow Protected', desc: 'Funds held securely until work is approved' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                      <item.icon className="h-5 w-5" weight="bold" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/signup')}
                  size="lg" 
                  className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="wallet-cta-btn"
                >
                  Start Earning
                  <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
                </Button>
                <div className="flex items-center gap-2 text-white/80">
                  <ShieldCheck className="w-5 h-5" weight="fill" />
                  <span className="text-sm font-medium">$25 minimum • No hidden fees</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      <FeedbackForm />

      {/* Final CTA */}
      <section className="py-16 bg-neutral-900 dark:bg-neutral-950 text-white border-t-4 border-[#D95800]" data-testid="tour-final-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black uppercase mb-6">Seen enough? Get started free.</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/signup?type=contractor')}
              size="lg"
              className="bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all font-bold"
              data-testid="tour-cta-contractor"
            >
              I'm a Contractor
            </Button>
            <Button
              onClick={() => navigate('/signup?type=worker')}
              size="lg"
              className="bg-white text-neutral-900 hover:bg-neutral-100 px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all font-bold"
              data-testid="tour-cta-worker"
            >
              I'm a Worker
            </Button>
          </div>
          <button
            onClick={() => navigate('/')}
            className="mt-8 text-neutral-400 hover:text-white text-sm font-bold underline underline-offset-4 transition-colors"
            data-testid="tour-back-home-link"
          >
            Back to home page
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home;
